import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import { Box, CircularProgress, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import type { PopoverProps } from "@mui/material/Popover";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { SystemDatePickerPopup } from "../../../../components/UIToolkit/Popups/SystemDatePickerPopup";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { NEUTRAL } from "../../../../theme/tokens";

export enum CellMenuAction {
	Edit = "edit",
	Delete = "delete",
	Cancel = "cancel",
	GoToProfile = "goToProfile",
}

type Props = {
	onSelectedAction: (action: CellMenuAction, onDeleteDate?: DateTime | null) => void;
	anchorEl?: PopoverProps["anchorEl"];
	open: boolean;
	isDeleteLoading?: boolean;
	/** Actions to omit from the menu (e.g. the Consecutive Master Schedule hides Delete). */
	hiddenActions?: CellMenuAction[];
};

export const RecurringMasterScheduleCellMenu: FC<Props> = ({ onSelectedAction, anchorEl, open, isDeleteLoading, hiddenActions }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const isMasterScheduleEditBlocked = chosenDepartment.disable_edit_master_schedule;
	const isHidden = useCallback((action: CellMenuAction) => hiddenActions?.includes(action) ?? false, [hiddenActions]);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [dateToDelete, setDateToDelete] = useState<DateTime | null>(DateTime.now().setZone(timezone));
	const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);

	const handleClose = useCallback(() => {
		setConfirmingDelete(false);
		onSelectedAction(CellMenuAction.Cancel);
	}, [onSelectedAction]);

	const handleDeleteClick = useCallback(() => {
		setConfirmingDelete(true);
	}, []);

	const handleDeleteForever = useCallback(() => {
		setConfirmingDelete(false);
		onSelectedAction(CellMenuAction.Delete, dateToDelete);
	}, [onSelectedAction, dateToDelete]);

	const handleCancelConfirm = useCallback(() => {
		setDatePickerAnchorEl(null);
		setConfirmingDelete(false);
	}, []);

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={handleClose}
			data-testid="master-schedule-cell-menu"
			slotProps={{
				list: {
					dense: true,
					autoFocusItem: open,
				},
			}}
			key="RecurringMasterScheduleCellMenu"
		>
			{confirmingDelete && (
				<SystemDatePickerPopup
					anchorEl={datePickerAnchorEl}
					open={!!datePickerAnchorEl}
					onClose={() => setDatePickerAnchorEl(null)}
					onChange={(date) => setDateToDelete(date)}
					value={dateToDelete}
					overridePickerProps={{
						minDate: DateTime.now().setZone(timezone).startOf("day"),
					}}
				/>
			)}
			{!isHidden(CellMenuAction.GoToProfile) && (
				<MenuItem
					key={"goToProfile"}
					onClick={() => onSelectedAction(CellMenuAction.GoToProfile)}
					disabled={isDeleteLoading}
					data-testid="master-schedule-menu-profile"
				>
					<ListItemIcon>
						<Person2OutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={t("open.profile")} />
				</MenuItem>
			)}
			{!isHidden(CellMenuAction.Edit) && (
				<InHouseTooltip title={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : ""} disableHoverListener={!isMasterScheduleEditBlocked}>
					<span>
						<MenuItem
							key={"edit"}
							onClick={() => onSelectedAction(CellMenuAction.Edit)}
							disabled={isDeleteLoading || isMasterScheduleEditBlocked}
							data-testid="master-schedule-menu-edit"
						>
							<ListItemIcon>
								<EditIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText primary={t("edit")} />
						</MenuItem>
					</span>
				</InHouseTooltip>
			)}
			{!isHidden(CellMenuAction.Delete) &&
				(confirmingDelete ? (
					<MenuItem key={"confirm-delete"} disableRipple={true} disableTouchRipple={true}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<Box sx={{ display: "flex", gap: 0.1 }}>
								<ListItemText primary={t("confirm")} sx={{ color: "error.main" }} />
								<ListItemButton
									key={"confirmDelete"}
									dense={true}
									sx={{
										color: "error.main",
										padding: 0,
										justifyContent: "center",
									}}
									tabIndex={0}
									onClick={handleDeleteForever}
								>
									<CheckIcon />
								</ListItemButton>
								<ListItemButton dense={true} sx={{ padding: 0, justifyContent: "center" }} tabIndex={0} onClick={handleCancelConfirm}>
									<CloseIcon />
								</ListItemButton>
							</Box>
							<ListItemButton
								dense={true}
								sx={{
									padding: "1px",
									border: `1px solid ${NEUTRAL[400]}`,
									borderRadius: "4px",
								}}
								onClick={(event) => setDatePickerAnchorEl(event.currentTarget)}
							>
								<ListItemText primary={`${t("remove.from")} ${dateToDelete?.toFormat("D")}`} />
							</ListItemButton>
						</Box>
					</MenuItem>
				) : (
					<InHouseTooltip title={isMasterScheduleEditBlocked ? t("schedule.template.edit.blocked") : ""} disableHoverListener={!isMasterScheduleEditBlocked}>
						<span>
							<MenuItem
								key={"delete"}
								onClick={handleDeleteClick}
								sx={{ color: "error.main" }}
								disabled={isDeleteLoading || isMasterScheduleEditBlocked}
								data-testid="master-schedule-menu-delete"
							>
								<ListItemIcon sx={{ color: "inherit" }}>
									<DeleteIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText primary={t("delete")} />
								{isDeleteLoading && <CircularProgress size={"12px"} sx={{ ml: 1 }} />}
							</MenuItem>
						</span>
					</InHouseTooltip>
				))}
		</Menu>
	);
};
